import React, { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface Brand {
  id: number;
  title: string;
}

interface SelectedBrand {
  id: number;
  title: string;
}

interface BrandAutocompleteProps {
  selectedBrands: SelectedBrand[];
  onBrandsChange: (brands: SelectedBrand[]) => void;
}

export const BrandAutocomplete: React.FC<BrandAutocompleteProps> = ({
  selectedBrands,
  onBrandsChange
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Brand[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounce pour la recherche
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (searchQuery.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/brands/search?q=${encodeURIComponent(searchQuery)}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.brands || []);
          setShowDropdown(true);
        }
      } catch (error) {
        console.error('Erreur recherche marques:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery]);

  const addBrand = (brand: Brand) => {
    // Vérifier si la marque n'est pas déjà sélectionnée
    if (!selectedBrands.find(b => b.id === brand.id)) {
      onBrandsChange([...selectedBrands, { id: brand.id, title: brand.title }]);
    }
    setSearchQuery('');
    setSuggestions([]);
    setShowDropdown(false);
  };

  const removeBrand = (brandId: number) => {
    onBrandsChange(selectedBrands.filter(b => b.id !== brandId));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">🏷️ Marques à inclure</label>

      {/* Marques sélectionnées */}
      {selectedBrands.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedBrands.map(brand => (
            <span
              key={brand.id}
              className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-100 text-cyan-700 text-sm font-medium"
            >
              {brand.title}
              <button
                type="button"
                onClick={() => removeBrand(brand.id)}
                className="ml-2 hover:text-cyan-900"
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Champ de recherche avec autocomplete */}
      <div ref={wrapperRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher une marque (ex: Nike, Zara, Sony...)"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          />
        </div>

        {/* Dropdown des suggestions */}
        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-3 text-sm text-gray-500">Recherche en cours...</div>
            ) : suggestions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">Aucune marque trouvée</div>
            ) : (
              suggestions.map(brand => (
                <button
                  key={brand.id}
                  type="button"
                  onClick={() => addBrand(brand)}
                  className="w-full px-4 py-2 text-left hover:bg-cyan-50 transition-colors text-sm"
                  disabled={selectedBrands.some(b => b.id === brand.id)}
                >
                  <span className={selectedBrands.some(b => b.id === brand.id) ? 'text-gray-400' : 'text-gray-900'}>
                    {brand.title}
                  </span>
                  {selectedBrands.some(b => b.id === brand.id) && (
                    <span className="ml-2 text-xs text-gray-400">(Déjà sélectionnée)</span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-2">
        💡 Tapez au moins 2 caractères pour rechercher une marque
      </p>
    </div>
  );
};
